import _ from 'lodash';
import sha1 from 'sha1';
import CryptoJS from 'crypto-js';
import { getItemType } from '../lib/commonUtils';
import { BaseEvent } from './events';

export const stepNameFormat = (stepName: string) => {
  const name = (stepName || '').replace('Step', '');
  return name.substring(0, 1).toLowerCase() + name.substring(1);
};

const DOMAINS = {
  authenticator: 'authenticator',
  submission: 'submission',
  contetnt: 'content',
  photoService: 'photoService',
};

const TypesWithPremiumIndicator = ['watch', 'bracelet', 'earrings', 'necklace'];

const hasPremiumIndicator = () => Object.keys(BaseEvent.dataObject).includes('isPremium');

const validateData = (methodName: string, dataKeys: string[]) => {
  const missing = BaseEvent.keysMissingFromDataObj(dataKeys);
  if (missing.length > 0) {
    const msg = `${methodName}, these params are missing: ${missing} , current data value: ${JSON.stringify(
      BaseEvent.dataObject,
    )}`;
    console.error(msg);
  }
};

type GASelect = {
  step: string;
  object?: string;
  value: any;
  object2?: string;
  value2?: string;
  object3?: string;
  value3?: string;
  object4?: string;
  value4?: string;
};

type GASelectPhotoUpload = {
  isWatchFunnel: boolean;
  value: 'none' | 'certificate' | 'photo' | 'photoAndCertificate';
  object2: string;
  value2?: string;
  object3?: string;
  value3?: string;
  object4?: string;
  value4: boolean;
};

class GA {
  static stepView(
    step: string,
    object?: string,
    value?: string,
    object2?: string,
    value2?: string,
    object3?: string,
    value3?: string,
  ) {
    const itemType = getItemType();
    const stepFormat = stepNameFormat(step);
    const formatSepVew = stepFormat.includes(itemType) ? stepFormat : `${itemType} ${stepFormat}`;
    let actionObject2;
    let actionValue2;
    let actionValue3 = _.camelCase(`${formatSepVew} StepView`);

    if (hasPremiumIndicator()) {
      actionObject2 = 'premiumFlow';
      actionValue2 = BaseEvent.dataObject.isPremium;
    }

    if (itemType === 'ring') {
      actionObject2 = object2;
      actionValue2 = value2;
    }

    if (value3 === 'watchPhotoUploadPreRegStepView') {
      actionValue3 = value3;
    }

    BaseEvent.open(
      [DOMAINS.submission, itemType, stepFormat],
      object,
      value,
      actionObject2,
      actionValue2,
      _.camelCase(`${itemType} SubmissionFunnel`),
      actionValue3,
    );
  }

  static registrationStepView(stepName: string) {
    const itemType = getItemType();
    const stepFormat = stepNameFormat(stepName);
    const formatSepVew = stepFormat.includes(itemType) ? stepFormat : `${itemType} ${stepFormat}`;

    let actionObject2;
    let actionValue2;

    if (hasPremiumIndicator()) {
      actionObject2 = 'premiumFlow';
      actionValue2 = BaseEvent.dataObject.isPremium;
    }
    BaseEvent.open(
      [DOMAINS.authenticator, stepFormat],
      undefined,
      undefined,
      actionObject2,
      actionValue2,
      _.camelCase(`${itemType} SubmissionFunnel`),
      _.camelCase(`${formatSepVew} StepView`),
    );
  }

  static premiumFlowStart(premium: boolean) {
    if (TypesWithPremiumIndicator.includes(getItemType())) {
      BaseEvent.setToDataObject({
        isPremium: premium,
      });
    }
  }

  static caratPopupView(step: string) {
    const itemType = getItemType();
    BaseEvent.open([DOMAINS.submission, itemType, stepNameFormat(step), 'caratDecimalPopup']);
  }

  static aiChatView() {
    BaseEvent.open(
      [DOMAINS.submission, 'submissionAi', 'aiFirstConversationScreen'],
      'aiSubmissionFunnel',
      'firstAiConversationScreenStepView',
    );
  }

  static flowTypeView() {
    BaseEvent.open([DOMAINS.submission, 'submissionFlowType']);
  }

  static tooltipView(step: string) {
    const itemType = getItemType();
    BaseEvent.navigate([DOMAINS.submission, itemType, stepNameFormat(step)], 'tooltip');
  }

  static onContinueChatClick() {
    BaseEvent.navigate(
      [DOMAINS.submission, 'submissionAi', 'aiFirstConversationScreen'],
      'aiSubmissionFunnel',
      'firstAiConversationScreenStepNext',
      'aiMsgType',
      'true',
    );
  }

  static onFlowTypeClick(flowType: 'submitByForm' | 'submitByAi') {
    BaseEvent.navigate(
      [DOMAINS.submission, 'submissionFlowType'],
      'submissionFlowTypeBtns',
      flowType,
    );
  }

  static aiFocus() {
    BaseEvent.focus(
      [DOMAINS.submission, 'submissionAi', 'aiFirstConversationScreen'],
      'aiSubmissionFunnel',
      'firstAiConversationScreenFocus',
      'aiTextInputField',
    );
  }

  static onSecondChatScreenLoad() {
    BaseEvent.open(
      [DOMAINS.submission, 'submissionAi', 'aiSecondConversationScreen'],
      'aiSubmissionFunnel',
      'secondAiConversationScreenStepView',
    );
  }

  static chatNext() {
    BaseEvent.navigate(
      [DOMAINS.submission, 'submissionAi', 'aiSecondConversationScreen'],
      'aiSubmissionFunnel',
      'secondAiConversationScreenStepNext',
    );
  }

  static inputMethodSelected(step: string, value: string) {
    const itemType = getItemType();
    BaseEvent.select([DOMAINS.submission, itemType, stepNameFormat(step)], 'inputMethod', value);
  }

  static autoRejectionView(object3?: string, value3?: string) {
    const itemType = getItemType();
    BaseEvent.open(
      [DOMAINS.submission, itemType, 'autoRejection'],
      undefined,
      undefined,
      undefined,
      undefined,
      itemType === 'ring' ? object3 : undefined,
      itemType === 'ring' ? value3 : undefined,
    );
  }

  static successSubmitByPhotoUpload(step: string) {
    BaseEvent.success([DOMAINS.submission, getItemType(), stepNameFormat(step)]);
  }

  static cashForGold() {
    BaseEvent.navigate([DOMAINS.submission, getItemType(), 'autoRejection'], 'cashForGold');
  }

  static submitNewItem() {
    const itemType = getItemType();
    const itemCase = _.replace(`rejection${_.startCase(itemType)}CtaBtn`, ' ', '');

    BaseEvent.navigate(
      [DOMAINS.submission, itemType, 'autoRejection'],
      'ctaSubmission',
      itemCase,
      'destination',
      'jewleryType',
    );
  }

  static typeSelectionView() {
    BaseEvent.open([DOMAINS.submission, 'jewleryType']);
  }

  /// click events
  static previousClick(step: string) {
    const itemType = getItemType();
    BaseEvent.navigate([DOMAINS.submission, itemType, stepNameFormat(step)], 'back');
  }

  static select({
    step, object, value, object3, value3,
    object4, value4,
  }: GASelect) {
    let object2;
    let value2;
    const itemType = getItemType();
    const stepFormat = stepNameFormat(step);
    const formatSepVew = stepFormat.includes(itemType) ? stepFormat : `${itemType} ${stepFormat}`;
    if (hasPremiumIndicator()) {
      object2 = 'premiumFlow';
      value2 = BaseEvent.dataObject.isPremium;
    }

    BaseEvent.select(
      [DOMAINS.submission, itemType, stepFormat],
      object || stepNameFormat(step),
      _.camelCase(value),
      object2,
      value2,
      _.camelCase(`${itemType} SubmissionFunnel`),
      _.camelCase(`${formatSepVew} StepNext`),
      object4,
      value4,
    );
  }

  static intercomClick(step: string) {
    const itemType = getItemType();
    BaseEvent.navigate([DOMAINS.submission, itemType, stepNameFormat(step)], 'intercom');
  }

  static submitItemType(value: string) {
    BaseEvent.select([DOMAINS.submission, 'typeSelection'], 'itemType', value);
  }

  static registrationSuccess(
    object?: string,
    value?: string,
    object2?: string,
    value2?: string,
    object4?: string,
    value4?: string,
  ) {
    const itemType = getItemType();
    let actionObject2;
    let actionValue2;

    if (itemType === 'ring') {
      actionObject2 = object2;
      actionValue2 = value2;
    }

    if (hasPremiumIndicator()) {
      actionObject2 = 'premiumFlow';
      actionValue2 = BaseEvent.dataObject.isPremium;
    }

    const dataKeys = [
      'userId',
      'userPhone',
      'userEmail',
      'userFullName',
      'emailSha1Encryption',
      'itemId',
    ];

    validateData('registrationSuccess', dataKeys);

    BaseEvent.success(
      [DOMAINS.authenticator, 'registration'],
      itemType === 'ring' ? object : undefined,
      itemType === 'ring' ? value : undefined,
      actionObject2,
      actionValue2,
      _.camelCase(`${itemType} SubmissionFunnel`),
      _.camelCase(`${itemType} registrationSuccessStep`),
      object4,
      value4,
    );
  }

  static submitNext(step: string, value: any, previousInput?: any, previousInputLocation?: string) {
    const itemType = getItemType();
    const location = [DOMAINS.submission, itemType, stepNameFormat(step)];
    let actionObject2;
    let actionValue2;
    if (hasPremiumIndicator()) {
      actionObject2 = 'premiumFlow';
      actionValue2 = BaseEvent.dataObject.isPremium;
    }
    if (previousInputLocation) {
      location.push(previousInputLocation);
    }
    const previousInputName = previousInput ? 'previousInput' : '';
    BaseEvent.input(
      location,
      stepNameFormat(step),
      value,
      actionObject2,
      actionValue2,
      previousInputName,
      previousInput,
    );
  }

  static submitGIANumber(page: string, step: string, value: any) {
    BaseEvent.input([DOMAINS.submission, page, 'certificate'], stepNameFormat(step), value);
  }

  static submitCaratWeight(range: string, classification: string) {
    const itemType = getItemType();
    const stepFormat = 'carat';
    const formatSepVew = `${itemType} ${stepFormat}`;
    let actionObject2;
    let actionValue2;
    if (hasPremiumIndicator()) {
      actionObject2 = 'premiumFlow';
      actionValue2 = BaseEvent.dataObject.isPremium;
    }

    BaseEvent.select(
      [DOMAINS.submission, getItemType(), stepFormat],
      stepFormat,
      range,
      actionObject2,
      actionValue2,
      _.camelCase(`${itemType} SubmissionFunnel`),
      _.camelCase(`${formatSepVew} StepNext`),
      'stoneTypeClassification',
      classification,

    );
  }

  static submitFlowType(value: 'submitByForm' | 'submitByImage') {
    BaseEvent.select(
      [DOMAINS.submission, getItemType(), 'submissionFlowType'],
      'submissionFlowTypeBtns',
      value,
      undefined,
      undefined,
      'ringSubmissionFunnel',
      'flowTypeNext',
    );
  }

  static submitByImg(step: string, classification: string) {
    BaseEvent.select(
      [DOMAINS.submission, getItemType(), stepNameFormat(step)],
      'carat',
      'imageInput',
      'stoneTypeClassification',
      classification,
      'ringSubmissionFunnel',
      'submitByImgStepNext',
    );
  }

  static successfullPhotoUpload() {
    let actionObject2;
    let actionValue2;
    if (hasPremiumIndicator()) {
      actionObject2 = 'premiumFlow';
      actionValue2 = BaseEvent.dataObject.isPremium;
    }
    BaseEvent.success([DOMAINS.photoService, 'photoUpload', 'photoUploaded'], undefined, undefined, actionObject2, actionValue2);
  }

  static successfullCertificateUpload() {
    let actionObject2;
    let actionValue2;
    if (hasPremiumIndicator()) {
      actionObject2 = 'premiumFlow';
      actionValue2 = BaseEvent.dataObject.isPremium;
    }
    BaseEvent.success([DOMAINS.photoService, 'photoUpload', 'certificateUploaded'], undefined, undefined, actionObject2, actionValue2);
  }

  static continueSelectPhotoUpload({
    isWatchFunnel,
    value,
    object2,
    value2,
    object3,
    value3,
    value4,
  }: GASelectPhotoUpload) {
    const itemType = getItemType();
    const stepFormat = 'photoUpload';
    let actionObject2 = object2;
    let actionValue2 = value2;
    let actionValue3 = _.camelCase(`${itemType} ${stepFormat}StepNext`);

    if (hasPremiumIndicator()) {
      actionObject2 = 'premiumFlow';
      actionValue2 = BaseEvent.dataObject.isPremium;
    }

    if (isWatchFunnel && value3) {
      actionValue3 = value3;
    }

    BaseEvent.select(
      [DOMAINS.photoService, stepFormat],
      'nextBtn',
      value,
      actionObject2,
      actionValue2,
      _.camelCase(`${itemType} SubmissionFunnel`),
      actionValue3,
      'mandatoryStep',
      value4,
    );
  }

  static continuePhotoUpload(value: 'none' | 'certificate' | 'photo' | 'photoAndCertificate') {
    BaseEvent.navigate([DOMAINS.photoService, 'photoUpload'], 'next', value);
  }

  static successfullGoogleRegister(isUserLogin: boolean) {
    if (isUserLogin) {
      BaseEvent.success([DOMAINS.authenticator, 'login', 'google']);
    } else {
      BaseEvent.success([DOMAINS.authenticator, 'registration', 'google']);
    }
  }

  static successfullItemSubmission() {
    let actionObject2;
    let actionValue2;
    if (hasPremiumIndicator()) {
      actionObject2 = 'premiumFlow';
      actionValue2 = BaseEvent.dataObject.isPremium;
    }
    BaseEvent.success([DOMAINS.submission, 'submitItem'], undefined, undefined, actionObject2, actionValue2);
  }

  static experimentEvent(experimentName: string, variantName: string) {
    BaseEvent.experiment({ experimentName, variantName });
  }

  static schedulingMethodSelectionStepView() {
    const itemType = getItemType();
    let actionObject2;
    let actionValue2;

    if (hasPremiumIndicator()) {
      actionObject2 = 'premiumFlow';
      actionValue2 = BaseEvent.dataObject.isPremium;
    }

    BaseEvent.open(
      [DOMAINS.submission, itemType, 'submissionSchedulingMethodSelection'],
      undefined,
      undefined,
      actionObject2,
      actionValue2,
      _.camelCase(`${itemType} SubmissionFunnel`),
      _.camelCase(`${itemType} MethodSelectionStepView`),
    );
  }

  static schedulingMethodSelectionStepNext(shippingMethod: string) {
    const itemType = getItemType();
    const methodValue = shippingMethod === 'pickup' ? 'fedexPickup' : 'fedexDropoff';
    let actionObject2;
    let actionValue2;

    if (hasPremiumIndicator()) {
      actionObject2 = 'premiumFlow';
      actionValue2 = BaseEvent.dataObject.isPremium;
    }

    BaseEvent.navigate(
      [DOMAINS.submission, itemType, 'submissionSchedulingMethodSelection'],
      undefined,
      undefined,
      actionObject2,
      actionValue2,
      _.camelCase(`${itemType} SubmissionFunnel`),
      _.camelCase(`${itemType} MethodSelectionStepNext`),
      'schedulingShipmentType',
      methodValue,
    );
  }

  static schedulingMethodSelectionStepSkip() {
    const itemType = getItemType();
    let actionObject2;
    let actionValue2;

    if (hasPremiumIndicator()) {
      actionObject2 = 'premiumFlow';
      actionValue2 = BaseEvent.dataObject.isPremium;
    }

    BaseEvent.navigate(
      [DOMAINS.submission, itemType, 'submissionSchedulingMethodSelection'],
      undefined,
      undefined,
      actionObject2,
      actionValue2,
      _.camelCase(`${itemType} SubmissionFunnel`),
      _.camelCase(`${itemType} MethodSelectionStepSkip`),
    );
  }

  static schedulingInsertInfoStepView(shippingMethod: string) {
    const itemType = getItemType();
    const methodValue = shippingMethod === 'pickup' ? 'fedexPickup' : 'fedexDropoff';
    let actionObject2;
    let actionValue2;

    if (hasPremiumIndicator()) {
      actionObject2 = 'premiumFlow';
      actionValue2 = BaseEvent.dataObject.isPremium;
    }

    BaseEvent.open(
      [DOMAINS.submission, itemType, 'submissionSchedulingInsertInfo'],
      undefined,
      undefined,
      actionObject2,
      actionValue2,
      _.camelCase(`${itemType} SubmissionFunnel`),
      _.camelCase(`${itemType} InsertInfoStepView`),
      'schedulingShipmentType',
      methodValue,
    );
  }

  static schedulingInsertInfoStepNext(shippingMethod: string) {
    const itemType = getItemType();
    const methodValue = shippingMethod === 'pickup' ? 'fedexPickup' : 'fedexDropoff';
    let actionObject2;
    let actionValue2;

    if (hasPremiumIndicator()) {
      actionObject2 = 'premiumFlow';
      actionValue2 = BaseEvent.dataObject.isPremium;
    }

    BaseEvent.navigate(
      [DOMAINS.submission, itemType, 'submissionSchedulingInsertInfo'],
      undefined,
      undefined,
      actionObject2,
      actionValue2,
      _.camelCase(`${itemType} SubmissionFunnel`),
      _.camelCase(`${itemType} InsertInfoStepNext`),
      'schedulingShipmentType',
      methodValue,
    );
  }

  static schedulingInsertInfoStepSkip() {
    const itemType = getItemType();
    let actionObject2;
    let actionValue2;

    if (hasPremiumIndicator()) {
      actionObject2 = 'premiumFlow';
      actionValue2 = BaseEvent.dataObject.isPremium;
    }

    BaseEvent.navigate(
      [DOMAINS.submission, itemType, 'submissionSchedulingInsertInfo'],
      undefined,
      undefined,
      actionObject2,
      actionValue2,
      _.camelCase(`${itemType} SubmissionFunnel`),
      _.camelCase(`${itemType} InsertInfoStepSkip`),
    );
  }

  static dataEvents = {
    itemSubmited: (data: { itemId: number; itemType: string }) => {
      BaseEvent.data(data);
    },
    userIdentified: (data: {
      userEmail: string;
      userFirstName: string;
      userLastName: string;
      userPhone: string;
      userId: number;
    }) => {
      BaseEvent.data({
        userPhone: data.userPhone,
        userEmail: data.userEmail,
        userId: data.userId,
        userFullName: `${data.userFirstName} ${data.userLastName}`,
        emailSha1Encryption: sha1(data.userEmail),
        emailSha256Encryption: CryptoJS.SHA256(data.userEmail).toString(),
        phoneSha256Encryption: CryptoJS.SHA256(data.userPhone).toString(),
      });
    },
    postRegEstimation: (data: {
      estimationValue: number;
      valueCategory: string;
      submissionQualification: number;
      ml1Factor: number;
      preregScore: number;
      ml4Score: number;
    }) => {
      BaseEvent.data({ ...data });
    },
    hqPurchase: (data: { postregScore: number; estimationValue: number }) => {
      const dataKeys = ['userId'];
      validateData('hqPurchase', dataKeys);
      BaseEvent.data(data);
    },
    ml4Purchase: (data: { ml4ConversionValue: number }) => {
      BaseEvent.data(data);
    },
    qualifiedSubmission: (data: {
      projectedLeadValue: number;
      itemsCount: number;
      daysSinceUserCreated: number;
    }) => {
      const dataKeys = [
        'userId',
        'userPhone',
        'userEmail',
        'userFullName',
        'emailSha1Encryption',
        'itemId',
      ];
      validateData('qualifiedSubmission', dataKeys);
      BaseEvent.data({
        projectedLeadValue: data.projectedLeadValue,
        itemsCount: data.itemsCount || 1,
        daysSinceUserCreated: data.daysSinceUserCreated || 0,
      });
    },
    googleData: (data: {
      userEmail: string;
      userFirstName: string;
      userLastName: string;
      userPhone: string;
    }) => {
      BaseEvent.data(data);
    },
  };
}

export default GA;
